import {
  Alert,
  Avatar,
  Box,
  CircularProgress,
  FormControl,
  FormHelperText,
  InputLabel,
  ListItemAvatar,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
  Tooltip,
  Typography,
} from "@mui/material";
import { HelpOutline } from "@mui/icons-material";
import React, { useEffect, useState } from "react";
import { fetchIntegrationsByType } from "../integrations/api";
import apolloLogo from "../../img/apollo.svg";

interface ApolloIntegration {
  id: string;
  type: string;
  name: string;
  instanceName?: string;
}

export default ({
  actionParameters,
  setActionParameters,
  setValidForm,
  getToken,
  companyId,
}: {
  actionParameters: any;
  setActionParameters: (p: any) => void;
  setValidForm: (b: boolean) => void;
  getToken: () => Promise<string>;
  companyId: string;
}) => {
  const [apolloIntegrations, setApolloIntegrations] = useState<
    ApolloIntegration[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch Apollo integrations
  useEffect(() => {
    if (!getToken || !companyId) return;

    const loadIntegrations = async () => {
      setLoading(true);
      setError(null);
      try {
        const integrations = await fetchIntegrationsByType(
          companyId,
          "apollo",
          getToken
        );
        setApolloIntegrations(integrations || []);

        // Auto-select if there's only one integration and no value selected
        if (
          integrations.length === 1 &&
          !actionParameters.apolloIntegrationId
        ) {
          setActionParameters({
            ...actionParameters,
            apolloIntegrationId: integrations[0].id,
          });
        }
      } catch (err) {
        console.error("Error fetching Apollo integrations:", err);
        setError("Failed to load Apollo integrations");
        setApolloIntegrations([]);
      } finally {
        setLoading(false);
      }
    };

    loadIntegrations();
  }, [getToken, companyId]);

  // Validate form
  useEffect(() => {
    const isValid = Boolean(actionParameters.apolloIntegrationId);
    setValidForm(isValid);
  }, [actionParameters.apolloIntegrationId, setValidForm]);

  const handleIntegrationChange = (event: SelectChangeEvent<string>) => {
    setActionParameters({
      ...actionParameters,
      apolloIntegrationId: event.target.value,
    });
  };

  const getIntegrationDisplayName = (integration: ApolloIntegration): string => {
    return integration.instanceName || integration.name || "Apollo";
  };

  const renderValue = (selectedId: string) => {
    if (!selectedId) {
      return <em>Select an Apollo integration</em>;
    }
    const integration = apolloIntegrations.find((i) => i.id === selectedId);
    if (!integration) {
      return <em>Select an Apollo integration</em>;
    }
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Avatar
          src={apolloLogo}
          sx={{ width: 24, height: 24 }}
          variant="rounded"
        />
        <span>{getIntegrationDisplayName(integration)}</span>
      </Box>
    );
  };

  return (
    <div>
      <div style={{ marginTop: "24px" }}>
        <Typography
          variant="subtitle1"
          sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}
        >
          Apollo Integration
          <Tooltip title="Select which Apollo.io integration to use for enrichment. The action will look up people using your Apollo API key.">
            <HelpOutline
              sx={{ fontSize: 16, color: "text.secondary", cursor: "help" }}
            />
          </Tooltip>
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && apolloIntegrations.length === 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            No Apollo integrations found. Please set up an Apollo integration in
            the Integrations settings first.
          </Alert>
        )}

        <FormControl
          fullWidth
          disabled={loading || apolloIntegrations.length === 0}
          required
        >
          <InputLabel>Apollo Integration</InputLabel>
          <Select
            value={actionParameters.apolloIntegrationId || ""}
            onChange={handleIntegrationChange}
            label="Apollo Integration"
            renderValue={renderValue}
            startAdornment={
              loading ? (
                <CircularProgress size={20} sx={{ mr: 1 }} />
              ) : undefined
            }
          >
            {loading && (
              <MenuItem value="" disabled>
                Loading integrations...
              </MenuItem>
            )}
            {!loading && apolloIntegrations.length === 0 && (
              <MenuItem value="" disabled>
                No Apollo integrations available
              </MenuItem>
            )}
            {apolloIntegrations.map((integration) => (
              <MenuItem key={integration.id} value={integration.id}>
                <ListItemAvatar sx={{ minWidth: 40 }}>
                  <Avatar
                    src={apolloLogo}
                    sx={{ width: 28, height: 28 }}
                    variant="rounded"
                  />
                </ListItemAvatar>
                <ListItemText primary={getIntegrationDisplayName(integration)} />
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>
            Person data will be enriched using this Apollo account
          </FormHelperText>
        </FormControl>
      </div>

      <div style={{ marginTop: "24px" }}>
        <Typography variant="subtitle1">Required Fields</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          To enrich a person, your list must have at least one of these
          combinations:
        </Typography>
        <ul
          style={{
            marginTop: "8px",
            paddingLeft: "20px",
            color: "text.secondary",
          }}
        >
          <li>
            <strong>First Name + Last Name + Company Name</strong> (or Domain)
          </li>
          <li>
            <strong>Email Address</strong>
          </li>
          <li>
            <strong>LinkedIn URL</strong>
          </li>
        </ul>
      </div>

      <div style={{ marginTop: "24px" }}>
        <Typography variant="subtitle1">Fields Enriched</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          The following fields will be populated (if found and currently empty):
        </Typography>
        <Box sx={{ display: "flex", gap: 4, mt: 1 }}>
          <ul
            style={{
              marginTop: "8px",
              paddingLeft: "20px",
              color: "text.secondary",
            }}
          >
            <li>Email</li>
            <li>Phone Number</li>
            <li>LinkedIn URL</li>
            <li>Headline / Job Title</li>
            <li>Location</li>
            <li>Seniority</li>
            <li>Department</li>
          </ul>
          <ul
            style={{
              marginTop: "8px",
              paddingLeft: "20px",
              color: "text.secondary",
            }}
          >
            <li>Company Name</li>
            <li>Company Website</li>
            <li>Company Industry</li>
            <li>Company Size</li>
            <li>Company Headquarters</li>
            <li>Company LinkedIn URL</li>
            <li>Company Logo URL</li>
          </ul>
        </Box>
      </div>
    </div>
  );
};
